import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { LineItem } from 'shared/types/cart';
import { ProjectSettings } from 'shared/types/projectSettings/ProjectSettings';
import { transformCartData } from '@/components/customercenter-ui/components/cart/utils';
import { PageTexts } from '@/components/customercenter-ui/components/order-details/i18n';
import { AvailabilityMsgDisplay } from '@/components/customercenter-ui/components/product/details/components/productDetails/availabilityMsg';
import Tags from '@/components/customercenter-ui/components/tags';
import { isPersonalizedItem, getPersonalizedText } from '@/components/customercenter-ui/components/utils/cartUtils';
import NoImage from '@/components/customercenter-ui/svgs/noImage';
import {
  daysBetweenCurrentAndDate,
  getArrivalAndBackorderDateFromResponse,
  isFutureDate,
} from '@/components/customercenter-ui/utils/fedexUtils';
import { useProjSettings } from '@/frontastic/hooks/useProjectSettings/useProjectSettings';
import { clearanceTextClass } from '@/helpers/utils/brandButton';
import { CartHelpers } from '@/helpers/utils/cartHelpers';
import { CurrencyHelpers } from '@/helpers/utils/currencyHelpers';
import { mapLocaleToMeaningfulFormat } from '@/helpers/utils/i18n';
import { getSelectedSite, TranslateText } from '@/helpers/utils/siteUtils';
import { DEFAULT_SITE_LANG, ORDER_TYPES } from '../../../../../../constants/constant';
import ContentStackImage from '../../../../common/contentStackImage/contentStackImage';
import Personalize from '../../../guestCheckout/personalize';

const OrderLineItem = ({
  cartItem,
  orderType,
  orderLineItems,
}: {
  cartItem: LineItem;
  orderType: string;
  orderLineItems: LineItem[];
}) => {
  const projectSettings: ProjectSettings = useProjSettings();
  const [shippingInfo, setShippingInfo] = useState<any>({});
  const CartData = transformCartData(cartItem);
  const orderDiscountShare = cartItem?.orderDiscountShare;
  const itemLevelDiscount = cartItem?.itemLevelDiscount;
  const totalItemLevelDiscount = CartHelpers.calculateTotalLineDiscount(orderDiscountShare, itemLevelDiscount);
  const totalItemLevelDiscountGreaterThanZero = (totalItemLevelDiscount?.centAmount ?? 0) > 0;
  const siteId = getSelectedSite() ?? 'ZBSite';

  const isMailOrderProduct = CartData?.itemNumber === '999999';
  const lineItemPrice = cartItem?.discountedPrice ?? cartItem?.price;
  const Texts = TranslateText(PageTexts, DEFAULT_SITE_LANG);
  let personalizeText = '';
  let isPersonalized = false;
  let parentItem = null;
  isPersonalized = isPersonalizedItem(cartItem);
  if (isPersonalized) {
    personalizeText = getPersonalizedText(cartItem);
  }
  if (cartItem?.lineGroupId) {
    parentItem = orderLineItems?.find((parentLineItem: LineItem) => {
      return parentLineItem.lineItemId === cartItem?.lineGroupId;
    });
    if (parentItem) {
      isPersonalized = isPersonalizedItem(parentItem);
    }
  }

  useEffect(() => {
    const storedShippingInfoStr = localStorage.getItem('cartItemsDate');
    let storedShippingInfo;
    try {
      storedShippingInfo = storedShippingInfoStr ? JSON.parse(storedShippingInfoStr) : null;
      setShippingInfo(storedShippingInfo);
    } catch (error) {
      console.error('Error parsing storedShippingInfo:', error);
    }
  }, []);

  const cartDeliveryDate = shippingInfo?.actualArrivalDate;
  const valueToClassName: any = {
    onlyLeft: 'text-stoneberry-onlyLeft',
    backOrdered: 'text-stoneberry-backordered',
    inStock: 'text-forestGreen',
  };
  const availabilityClassName = valueToClassName[cartItem?.availabilityData?.value as string] ?? 'text-[#DF2429]';
  const deliveryName = shippingInfo?.name;
  const shippingMethodErrorFlag =
    (cartItem?.variant?.attributes?.priorityShipAvail === false &&
      deliveryName?.toLowerCase() === mapLocaleToMeaningfulFormat().priorityShippingMethod) ||
    (cartItem?.variant?.attributes?.expressShipAvail === false &&
      deliveryName?.toLowerCase() === mapLocaleToMeaningfulFormat().expressShippingMethod);
  const showShippingError = shippingMethodErrorFlag;

  const isOnBackOrder = cartItem?.availabilityData?.value === 'backOrdered';
  const isInStockWithBackOrder = (isOnBackOrder && cartItem?.variant?.isOnStock) ?? false;
  const dropShipValue = cartItem?.variant?.attributes?.dropShipEligible || false;
  const backOrderDateWithYear = isOnBackOrder ? cartItem?.availabilityData?.backOrderDateWithYear : '';
  let cartItemDeliveryDate = '';
  let cartItemStandardDeliveryDate = '';
  let cartItemStandardBackorderDate = '';
  let cartItemBackorderDate = '';
  if (cartDeliveryDate) {
    let intervalDays = 0;
    if (backOrderDateWithYear && isFutureDate(backOrderDateWithYear)) {
      intervalDays = daysBetweenCurrentAndDate(backOrderDateWithYear);
    }
    const { arrivalDate, backorderDate } = getArrivalAndBackorderDateFromResponse(
      cartDeliveryDate,
      dropShipValue,
      isPersonalized,
      projectSettings,
      false,
      intervalDays,
    );
    if (shippingInfo?.standardDeliveryDate) {
      const { arrivalDate: standardArrivalDate, backorderDate: standardBackOrderDate } =
        getArrivalAndBackorderDateFromResponse(
          shippingInfo?.standardDeliveryDate,
          dropShipValue,
          isPersonalized,
          projectSettings,
          false,
          intervalDays,
        );
      cartItemStandardDeliveryDate = isInStockWithBackOrder ? standardArrivalDate : standardBackOrderDate;
      cartItemStandardBackorderDate = standardBackOrderDate;
    }
    cartItemDeliveryDate = isInStockWithBackOrder ? arrivalDate : backorderDate;
    cartItemBackorderDate = backorderDate;
  }

  return (
    <div className="grid w-full grid-cols-4 justify-between rounded-md border-b border-foggyGray px-5 pb-10 pt-5 text-base">
      <section className="col-span-2 flex">
        {CartData?.productImg ? (
          <Link prefetch={false} href={CartData?._url || '#'} passHref title={CartData?.name ?? ''}>
            <ContentStackImage
              className="w-full"
              src={CartData?.productImg}
              imageWidth={120}
              imageHeight={120}
              alt={`Product Image ${CartData?.itemNumber}`}
              loading="eager"
            />
          </Link>
        ) : (
          <NoImage width={120} height={120} />
        )}
        <section className="mx-4">
          <Link prefetch={false} href={CartData?._url || '#'} passHref>
            <p
              className="max-w-md text-lg font-bold hover:underline"
              dangerouslySetInnerHTML={{ __html: CartData?.name || '' }}
            />
          </Link>
          <p className="text-sm font-regular">
            {Texts.itemNumber} {CartData?.itemNumber}
          </p>
          {!isMailOrderProduct && (
            <p className="text-sm font-regular">
              {CartData?.color} | {CartData?.size}
            </p>
          )}
          {CartData?.listPrice?.centAmount === CartData?.originalPrice?.centAmount || isMailOrderProduct ? (
            <p className="mt-2 text-lg font-bold">{CurrencyHelpers.formatForCurrency(lineItemPrice as any)}</p>
          ) : CartData?.price?.centAmount !== CartData?.listPrice?.centAmount ? (
            <div className="mt-2">
              {CartData?.activePriceType && (
                <Tags activePriceType={CartData?.activePriceType} overrideTagLabel={CartData?.priceLabel} />
              )}
              <p className="flex gap-2 text-lg">
                <span className={`font-bold ${clearanceTextClass(siteId)}`}>
                  {CurrencyHelpers.customCurrencyFormat(CartData?.price)}
                </span>
                <span className="text-[#666666] line-through">
                  {CurrencyHelpers.customCurrencyFormat(
                    CartData?.price?.centAmount === CartData?.originalPrice?.centAmount
                      ? CartData?.listPrice
                      : CartData?.originalPrice,
                  )}
                </span>
              </p>
            </div>
          ) : (
            <p className="mt-2 flex gap-2 text-lg font-bold">
              <span className={CartData?.originalPrice > 0 ? 'line-through' : 'hidden'}>
                {CurrencyHelpers.customCurrencyFormat(CartData?.originalPrice)}
              </span>
              <span>{CurrencyHelpers.customCurrencyFormat(CartData?.price)}</span>
            </p>
          )}
        </section>
      </section>

      <section>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2">Qty: {CartData?.count}</div>
        </div>
        <section className="pb-4">
          {cartItem?.availabilityData?.message && (
            <AvailabilityMsgDisplay
              isInStockWithBackOrder={isInStockWithBackOrder}
              availabilityMsg={cartItem?.availabilityData?.message}
              arrivalDate={showShippingError ? cartItemStandardDeliveryDate : cartItemDeliveryDate ?? ''}
              backOrderedArrivalDate={showShippingError ? cartItemStandardBackorderDate : cartItemBackorderDate}
              availabilityClassName={availabilityClassName}
              isCheckout={true}
            />
          )}
        </section>
      </section>

      <section className="text-right">
        <p>
          {Texts.totalPrice}
          <span className="font-bold text-black">
            {' '}
            {orderType === ORDER_TYPES.NO_CHARGE
              ? '$0.00'
              : CartHelpers.calculateTotalPrice(lineItemPrice, cartItem?.count)}
          </span>
        </p>
        {totalItemLevelDiscountGreaterThanZero && (
          <p className="mr-2 leading-[20.8pxpx] text-black">
            <span>{`${mapLocaleToMeaningfulFormat().discount}:`}</span>{' '}
            <strong className={`${clearanceTextClass(siteId)}`}>
              {CurrencyHelpers.formatForCurrency(totalItemLevelDiscount, true)}
            </strong>
          </p>
        )}
      </section>
      {personalizeText && (
        <section className="col-span-6 mt-4 flex justify-end">
          <Personalize personalizeText={personalizeText} />
        </section>
      )}
    </div>
  );
};

export default OrderLineItem;
